import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { CircleOff } from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useDiseaseHistory } from "../../DiseaseHistoryContext";
import type { BreastTestingDetail } from "../../DiseaseHistoryContext";
import type { DiseaseHistoryStackParamList } from "../../DiseaseHistoryNavigator";
import { useFlowProgress } from "../../useFlowProgress";
import { BREAST_TEST_OPTIONS } from "../options";
import type { BreastTestType } from "../options";

export function BreastTestingScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useDiseaseHistory();
  const navigation =
    useNavigation<NativeStackNavigationProp<DiseaseHistoryStackParamList>>();
  const progress = useFlowProgress("BreastTesting");

  const selectedTests = useMemo(
    () => new Set(state.breastTestings.map((t) => t.testType)),
    [state.breastTestings],
  );

  const isNone = state.hasBreastTestings === false;

  const toggleTest = (testType: BreastTestType) => {
    if (selectedTests.has(testType)) {
      // Remove
      const toRemove = state.breastTestings.find(
        (t) => t.testType === testType,
      );
      const idsToDelete = toRemove?.id
        ? [...state.breastTestingIdsToDelete, toRemove.id]
        : state.breastTestingIdsToDelete;
      const remaining = state.breastTestings.filter(
        (t) => t.testType !== testType,
      );
      updateState({
        hasBreastTestings: remaining.length > 0 ? true : null,
        breastTestings: remaining,
        breastTestingIdsToDelete: idsToDelete,
      });
    } else {
      // Add
      const newTest: BreastTestingDetail = { testType };
      updateState({
        hasBreastTestings: true,
        breastTestings: [...state.breastTestings, newTest],
      });
    }
  };

  const handleNone = () => {
    const idsToDelete = state.breastTestings
      .filter((t) => t.id)
      .map((t) => t.id!);
    updateState({
      hasBreastTestings: false,
      breastTestings: [],
      breastTestingIdsToDelete: [
        ...state.breastTestingIdsToDelete,
        ...idsToDelete,
      ],
    });
  };

  const handleSave = async () => {
    await save();
    const currentIdx = state.navigationStack.indexOf("BreastTesting");
    const nextRoute = state.navigationStack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  const isValid =
    isNone ||
    (state.hasBreastTestings === true && state.breastTestings.length > 0);

  return (
    <QuestionLayout
      title={t("med_history.medical.disease_history.breast_testing.title")}
      subtitle={t(
        "med_history.medical.disease_history.breast_testing.subtitle",
      )}
      onBack={() => navigation.goBack()}
      progress={progress}
      onSave={handleSave}
      saveDisabled={!isValid}
      saveLabel={t("med_history.medical.shared.next")}
    >
      <View style={styles.container}>
        <View style={styles.grid}>
          {BREAST_TEST_OPTIONS.map((option) => (
            <View key={option.id} style={styles.cell}>
              <SquareOptionButton
                icon={option.icon}
                label={t(option.labelKey)}
                isSelected={!isNone && selectedTests.has(option.id)}
                onPress={() => {
                  if (isNone) {
                    updateState({
                      hasBreastTestings: true,
                      breastTestings: [{ testType: option.id }],
                    });
                  } else {
                    toggleTest(option.id);
                  }
                }}
                labelSize="sm"
              />
            </View>
          ))}
          <View style={styles.cell}>
            <SquareOptionButton
              icon={CircleOff}
              label={t("med_history.medical.shared.none")}
              isSelected={isNone}
              onPress={handleNone}
              labelSize="sm"
            />
          </View>
        </View>
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    width: "100%",
  },
  cell: {
    width: "47%",
  },
});
